/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import { Box, IconButton, Stack, Tooltip, Typography, TextField } from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import EditIcon from "@mui/icons-material/Edit";
import { Effect, Nullable, Setter } from "../../utils/Interfaces";
import { modifyField } from "./formControlsFunctions";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

export interface AmendableEditableParagraphProps<ParentModel> {
    field: Nullable<string>;
    setField: Setter<Nullable<string>>;
    parentField: Nullable<string>;
    setParentField: Setter<Nullable<string>>;
    updatePath: string;
    label: string;
    name: keyof ParentModel;
    parentName: keyof ParentModel;
    originalDescription: Nullable<string>;
    onUpdate: Effect;
    editable?: boolean;
}

const AmendableEditableParagraph = <ParentModel,>(props: AmendableEditableParagraphProps<ParentModel>) => {
    const {
        field,
        setField,
        parentField,
        setParentField,
        updatePath,
        label,
        name,
        parentName,
        originalDescription,
        onUpdate,
        editable,
    } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [editing, setEditing] = useState<boolean>(false);
    const [localField, setLocalField] = useState<Nullable<string>>(field);

    const [editingParent, setEditingParent] = useState<boolean>(false);
    const [localParentField, setLocalParentField] = useState<Nullable<string>>(parentField);

    useEffect(() => {
        setLocalField(field);
        setLocalParentField(parentField);
    }, [field, parentField]);

    let darkTheme = localStorage.getItem("techwiseTheme") === "dark";

    return (
        <Box sx={{ backgroundColor: darkTheme ? "#303030" : "#F2F2F2", borderRadius: 2, pl: 2, pr: 2, pt: 0.1, pb: 0.25, mb: 2 }}>
            <Box sx={{ marginBottom: "20px" }} />

            <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 18, fontWeight: "bold" }} color="text.primary">
                    {label}
                </Typography>

                {!editingParent && (
                    <Tooltip title={`Edit ${label}`}>
                        <IconButton
                            disabled={!editable}
                            onClick={() => {
                                setEditingParent(true);
                            }}
                        >
                            <EditIcon color={!editable ? "secondary" : "error"} />
                        </IconButton>
                    </Tooltip>
                )}

                {editingParent && (
                    <Stack direction={"row"}>
                        <Tooltip title={"Cancel Changes"}>
                            <IconButton
                                onClick={() => {
                                    setEditingParent(false);
                                    setLocalParentField(parentField);
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={"Save Changes"}>
                            <IconButton
                                onClick={() => {
                                    setEditingParent(false);
                                    setParentField(localParentField);
                                    modifyField<ParentModel>(
                                        updatePath,
                                        parentName as keyof ParentModel,
                                        localParentField,
                                        dispatch,
                                        onUpdate
                                    );
                                }}
                            >
                                <CheckIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            </Stack>

            <Box sx={{ marginBottom: "10px" }} />

            {!editingParent && (
                <Box>
                    <Typography sx={{ color: "burlywood" }}>
                        {originalDescription === undefined || originalDescription === null || originalDescription === ""
                            ? "--"
                            : originalDescription}
                    </Typography>
                </Box>
            )}

            {editingParent && (
                <TextField
                    multiline
                    rows={4}
                    fullWidth
                    margin="normal"
                    id={parentName as string}
                    name={parentName as string}
                    value={localParentField}
                    label={`${label}`}
                    size="small"
                    onChange={(event) => setLocalParentField(event.target.value)}
                />
            )}

            <Box sx={{ marginBottom: "20px" }} />

            <Stack
                direction={"row"}
                justifyContent={"space-between"}
                sx={{
                    marginTop: "10px",
                }}
            >
                <Typography sx={{ fontWeight: "bold" }}>{"Amendments"}</Typography>
                {!editing && (
                    <Tooltip title={`Edit ${label} Amendments`}>
                        <IconButton
                            onClick={() => {
                                setEditing(true);
                            }}
                            disabled={!editable}
                        >
                            <CommentIcon />
                        </IconButton>
                    </Tooltip>
                )}
                {editing && (
                    <Stack direction={"row"}>
                        <Tooltip title={"Cancel Changes"}>
                            <IconButton
                                onClick={() => {
                                    setEditing(false);
                                    setLocalField(field);
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={"Save Changes"}>
                            <IconButton
                                onClick={() => {
                                    setEditing(false);
                                    setField(localField);
                                    modifyField<ParentModel>(
                                        updatePath,
                                        name as keyof ParentModel,
                                        localField,
                                        dispatch,
                                        onUpdate
                                    );
                                }}
                            >
                                <CheckIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            </Stack>

            {!editing && (
                <Typography color="text.primary">
                    {localField === undefined || localField === null || localField === "" ? "--" : localField}
                </Typography>
            )}
            {editing && (
                <TextField
                    multiline
                    rows={4}
                    fullWidth
                    margin="normal"
                    id={name as string}
                    name={name as string}
                    value={localField}
                    label={`${label} Amendments`}
                    size="small"
                    onChange={(event) => setLocalField(event.target.value)}
                />
            )}
            <Box sx={{ marginBottom: "20px" }} />
        </Box>
    );
};

export default AmendableEditableParagraph;
